import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronLeft, FileText, PlayCircle, ExternalLink, Link as LinkIcon, Pencil, CheckCircle2, Globe, Clock } from "lucide-react";

function getEmbedUrl(url) {
  if (!url) return null;
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
}

function ResourceCard({ resource }) {
  const Icon =
    resource.resource_type === "video" ? PlayCircle :
    resource.resource_type === "pdf" ? FileText : LinkIcon;

  const fileSrc = resource.url_type === "url" ? resource.url : `${API}/files/${resource.url}`;

  if (resource.resource_type === "video") {
    const embed = getEmbedUrl(resource.url);
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden" data-testid={`resource-${resource.id}`}>
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-[#1B2E7A]" strokeWidth={1.5} />
          <h4 className="text-sm font-medium text-[#1A1A1A]">{resource.title}</h4>
        </div>
        <div className="video-shell">
          {embed ? (
            <iframe src={embed} title={resource.title} allow="autoplay; encrypted-media" allowFullScreen />
          ) : resource.url_type === "upload" ? (
            <video controls src={fileSrc} />
          ) : (
            <div className="flex items-center justify-center h-48 text-white">
              <a href={resource.url} target="_blank" rel="noreferrer" className="underline">
                Abrir video
              </a>
            </div>
          )}
        </div>
        {resource.notes && (
          <p className="px-4 py-2 text-xs text-[#737373] bg-[#FFFFFF]">{resource.notes}</p>
        )}
      </div>
    );
  }

  if (resource.resource_type === "pdf") {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden" data-testid={`resource-${resource.id}`}>
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-4 w-4 text-[#1B2E7A] shrink-0" strokeWidth={1.5} />
            <h4 className="text-sm font-medium text-[#1A1A1A] truncate">{resource.title}</h4>
          </div>
          <a
            href={fileSrc}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-[#1B2E7A] hover:underline inline-flex items-center gap-1 shrink-0"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Abrir
          </a>
        </div>
        <iframe src={fileSrc} title={resource.title} className="w-full" style={{ height: "60vh" }} />
        {resource.notes && (
          <p className="px-4 py-2 text-xs text-[#737373] bg-[#FFFFFF]">{resource.notes}</p>
        )}
      </div>
    );
  }

  // link
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noreferrer"
      data-testid={`resource-${resource.id}`}
      className="bg-white border border-[#E5E7EB] rounded-md p-4 flex items-start gap-3 hover:shadow-md hover:border-[#E5E7EB] transition-all"
    >
      <div className="h-10 w-10 rounded-md bg-[#F3F4F6] flex items-center justify-center shrink-0">
        <LinkIcon className="h-5 w-5 text-[#1B2E7A]" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-[#1A1A1A] mb-0.5">{resource.title}</h4>
        <p className="text-xs text-[#6B7280] truncate">{resource.url}</p>
        {resource.notes && <p className="text-xs text-[#737373] mt-1">{resource.notes}</p>}
      </div>
      <ExternalLink className="h-4 w-4 text-[#6B7280] shrink-0 mt-1" />
    </a>
  );
}

export default function PartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/financing-partners/${id}`);
        setPartner(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="text-[#374151]">Cargando...</div>;
  if (!partner) return <div className="text-[#374151]">Socio no encontrado.</div>;

  const accent = partner.color || "#1B2E7A";

  return (
    <div data-testid="partner-detail-page">
      <button
        onClick={() => navigate("/financiamiento")}
        className="inline-flex items-center gap-1 text-sm text-[#374151] hover:text-[#1A1A1A] mb-6"
        data-testid="back-to-partners"
      >
        <ChevronLeft className="h-4 w-4" /> Socios de financiamiento
      </button>

      {/* Brand header */}
      <div
        className="rounded-md overflow-hidden border border-[#E5E7EB] mb-8 fade-up"
        style={{ background: `linear-gradient(120deg, ${accent} 0%, ${accent}D9 60%, #FFFFFF 60%)` }}
      >
        <div className="p-6 md:p-8 flex flex-col md:flex-row gap-6 md:items-center">
          <div className="md:w-2/5 text-white relative z-10">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 mb-2">Socio de financiamiento</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">{partner.name}</h1>
            {partner.tagline && (
              <p className="text-base text-white/85 mt-2 italic">{partner.tagline}</p>
            )}
          </div>
          <div className="flex-1 md:py-2">
            <p className="text-sm text-[#374151] leading-relaxed mb-4">{partner.summary}</p>
            <div className="flex flex-wrap gap-2 text-xs">
              {partner.approval_time && (
                <span className="inline-flex items-center gap-1.5 bg-white border border-[#E5E7EB] px-2.5 py-1.5 rounded-md text-[#374151]">
                  <Clock className="h-3.5 w-3.5 text-[#1B2E7A]" /> Aprobación: <strong>{partner.approval_time}</strong>
                </span>
              )}
              {partner.credit_required && (
                <span className="inline-flex items-center gap-1.5 bg-white border border-[#E5E7EB] px-2.5 py-1.5 rounded-md text-[#374151]">
                  Crédito: <strong>{partner.credit_required}</strong>
                </span>
              )}
              {partner.website && (
                <a
                  href={partner.website}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 bg-white border border-[#E5E7EB] px-2.5 py-1.5 rounded-md text-[#1B2E7A] hover:bg-[#F3F4F6]"
                >
                  <Globe className="h-3.5 w-3.5" /> Sitio oficial <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {user?.role === "admin" && (
                <Link
                  to="/admin/financiamiento"
                  className="inline-flex items-center gap-1.5 bg-[#1B2E7A] text-white px-2.5 py-1.5 rounded-md hover:bg-[#13205A]"
                >
                  <Pencil className="h-3.5 w-3.5" /> Editar materiales
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Key features */}
      {partner.key_features?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-6 mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-4">Características clave</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {partner.key_features.map((f, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#1B2E7A] mt-0.5 shrink-0" strokeWidth={2} />
                <p className="text-sm text-[#1A1A1A]">{f}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Materials */}
      <div className="mb-2">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-1">Materiales</p>
        <h2 className="font-heading text-2xl text-[#1A1A1A] mb-4">
          PDFs, videos y enlaces ({partner.resources?.length || 0})
        </h2>
      </div>

      {(!partner.resources || partner.resources.length === 0) ? (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-8 text-center text-[#737373]" data-testid="no-resources">
          Este socio aún no tiene materiales cargados.
          {user?.role === "admin" && (
            <p className="mt-2">
              <Link to="/admin/financiamiento" className="text-[#1B2E7A] underline">Cárgalos desde el panel admin</Link>
            </p>
          )}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          {partner.resources.map((r) => (
            <ResourceCard key={r.id} resource={r} />
          ))}
        </div>
      )}
    </div>
  );
}
